import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import logger from '@/utils/logger';

const API = '/api';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      // Clear the hash immediately to prevent re-processing on re-renders
      if (window.history.replaceState) {
        window.history.replaceState(null, '', window.location.pathname);
      }

      if (!sessionId) {
        navigate('/', { replace: true });
        return;
      }

      try {
        const res = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { timeout: 15000 }
        );
        const { user, token } = res.data;

        if (!user || !token) {
          throw new Error('Invalid session response');
        }

        // httpOnly cookie is set by the backend automatically
        login(user, token);

        // Small delay to ensure React state has propagated before navigating
        await new Promise(resolve => setTimeout(resolve, 100));

        const dest = user.onboarding_complete ? '/dashboard' : '/onboarding';
        navigate(dest, { replace: true });
      } catch (err) {
        logger.error('Auth callback error:', err);
        setError('Login failed. Please try again.');

        // Clear any partial auth state (remove legacy token if present)
        localStorage.removeItem('nl_token');

        // Redirect to landing after a brief error display
        setTimeout(() => navigate('/', { replace: true }), 2000);
      }
    };

    processAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] dark:bg-[#141C22]">
      <div className="text-center">
        {error ? (
          <>
            <div className="w-12 h-12 rounded-full bg-[#C67A5C]/10 flex items-center justify-center mx-auto mb-4">
              <span className="text-[#C67A5C] text-xl">!</span>
            </div>
            <p className="text-[#C67A5C] font-medium">{error}</p>
            <p className="text-sm text-[#7A7A72] mt-2">Redirecting...</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-4 border-[#1A3A4A] dark:border-[#6B9E8A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[#7A7A72] dark:text-[#A1A1AA] font-medium">Signing you in...</p>
          </>
        )}
      </div>
    </div>
  );
}
